/**
 * ProfessionalDashboard - Dashboard for Retailers, Fabricators, Advisors
 * ======================================================================
 * Manage requests, materials, slab photos, and analytics
 * 
 * @version 1.0
 * Dark theme - CMS-driven content
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, MessageSquare, Package, Camera, BarChart3,
  Settings, ChevronRight, Clock, Check, AlertCircle, Loader2,
  Eye, Phone, Mail, MapPin, Star, Building2, Upload, X,
  Filter, Search, TrendingUp, Users, ExternalLink, Plus,
  FileImage, Trash2, Edit, Send, Archive
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useAuth } from '../App';
import supabase from '../lib/supabase';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Status colors and labels
const REQUEST_STATUS = {
  new: { label: 'Nuova', color: 'bg-green-500/20 text-green-400', icon: AlertCircle },
  read: { label: 'Letta', color: 'bg-[var(--gold)]/20 text-[var(--gold)]', icon: Eye },
  in_progress: { label: 'In corso', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  responded: { label: 'Risposta', color: 'bg-[#B08B5C]/20 text-[#B08B5C]', icon: Check },
  closed: { label: 'Chiusa', color: 'bg-white/10 text-white/50', icon: Archive },
  spam: { label: 'Spam', color: 'bg-red-500/20 text-red-400', icon: Trash2 }
};

const REQUEST_TYPES = {
  info: 'Informazioni',
  foto_lastra: 'Foto lastra',
  disponibilita: 'Disponibilità',
  appuntamento: 'Appuntamento',
  preventivo: 'Preventivo',
  campione: 'Campione'
};

/**
 * Stat Card Component
 */
const StatCard = ({ title, value, subtitle, icon: Icon, trend }) => (
  <div className="bg-[#121212] border border-[#1a1a1a] rounded-xl p-5">
    <div className="flex items-start justify-between mb-4">
      <div className="p-3 bg-[#B08B5C]/10 rounded-lg">
        <Icon className="w-5 h-5 text-[#B08B5C]" />
      </div>
      {trend && (
        <span className={`text-xs px-2 py-1 rounded ${trend > 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <h3 className="text-2xl font-bold text-white mb-1">{value?.toLocaleString() || 0}</h3>
    <p className="text-sm text-white/50">{title}</p>
    {subtitle && <p className="text-xs text-white/30 mt-1">{subtitle}</p>}
  </div>
);

/**
 * Request Card Component
 */
const RequestCard = ({ request, onView, onRespond, onUpdateStatus }) => {
  const status = REQUEST_STATUS[request.status] || REQUEST_STATUS.new;
  const StatusIcon = status.icon;
  
  return (
    <div className="bg-[#121212] border border-[#1a1a1a] rounded-xl p-4 hover:border-[#B08B5C]/30 transition-all">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`px-2 py-1 rounded text-xs font-medium ${status.color}`}>
            <StatusIcon className="w-3 h-3 inline mr-1" />
            {status.label}
          </span>
          <span className="text-xs text-white/40 bg-white/5 px-2 py-1 rounded">
            {REQUEST_TYPES[request.request_type] || request.request_type}
          </span>
        </div>
        <span className="text-xs text-white/30">
          {new Date(request.created_at).toLocaleDateString('it-IT')}
        </span>
      </div>
      
      <h4 className="text-white font-medium mb-1">{request.contact_name}</h4>
      <p className="text-white/50 text-sm mb-2">{request.contact_email}</p>
      
      {request.material_name && (
        <p className="text-[#B08B5C] text-sm mb-2">
          Materiale: {request.material_name}
        </p>
      )}
      
      {request.message && (
        <p className="text-white/40 text-sm line-clamp-2 mb-3">{request.message}</p>
      )}
      
      <div className="flex gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onView(request)}
          className="flex-1 border-[#1a1a1a] text-white/60 hover:bg-[#1a1a1a] text-xs"
        >
          <Eye className="w-3 h-3 mr-1" /> Dettagli
        </Button>
        {request.status !== 'responded' && request.status !== 'closed' && (
          <Button
            size="sm"
            onClick={() => onRespond(request)}
            className="flex-1 bg-[#B08B5C] text-black hover:bg-[#C9A46E] text-xs"
          >
            <Send className="w-3 h-3 mr-1" /> Rispondi
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * Request Detail Modal
 */
const RequestDetailModal = ({ request, onClose, onRespond, onUpdateStatus }) => {
  if (!request) return null;
  
  const status = REQUEST_STATUS[request.status] || REQUEST_STATUS.new;
  const StatusIcon = status.icon;
  
  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] rounded-2xl border border-[#1a1a1a] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-[#1a1a1a]">
          <div>
            <h3 className="text-xl font-semibold text-white">Dettaglio richiesta</h3>
            <p className="text-white/40 text-sm mt-1">
              {new Date(request.created_at).toLocaleString('it-IT')}
            </p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="p-6 space-y-6">
          {/* Status */}
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1.5 rounded text-sm font-medium ${status.color}`}>
              <StatusIcon className="w-4 h-4 inline mr-1" />
              {status.label}
            </span>
            <span className="text-white/50 text-sm bg-white/5 px-3 py-1.5 rounded">
              {REQUEST_TYPES[request.request_type] || request.request_type}
            </span>
          </div>
          
          {/* Contact Info */}
          <div className="bg-[#121212] rounded-xl p-4 border border-[#1a1a1a]">
            <h4 className="text-white font-medium mb-3">Contatto</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-white/40 text-xs mb-1">Nome</p>
                <p className="text-white">{request.contact_name}</p>
              </div>
              <div>
                <p className="text-white/40 text-xs mb-1">Email</p>
                <a href={`mailto:${request.contact_email}`} className="text-[#B08B5C] hover:underline">
                  {request.contact_email}
                </a>
              </div>
              {request.contact_phone && (
                <div>
                  <p className="text-white/40 text-xs mb-1">Telefono</p>
                  <a href={`tel:${request.contact_phone}`} className="text-[#B08B5C] hover:underline">
                    {request.contact_phone}
                  </a>
                </div>
              )}
              {request.company_name && (
                <div>
                  <p className="text-white/40 text-xs mb-1">Azienda</p>
                  <p className="text-white">{request.company_name}</p>
                </div>
              )}
            </div>
          </div>
          
          {/* Material */}
          {request.material_name && (
            <div className="bg-[#121212] rounded-xl p-4 border border-[#1a1a1a]">
              <h4 className="text-white font-medium mb-2">Materiale richiesto</h4>
              <p className="text-[#B08B5C]">{request.material_name}</p>
            </div>
          )}
          
          {/* Message */}
          {request.message && (
            <div className="bg-[#121212] rounded-xl p-4 border border-[#1a1a1a]">
              <h4 className="text-white font-medium mb-2">Messaggio</h4>
              <p className="text-white/70 whitespace-pre-wrap">{request.message}</p>
            </div>
          )}
          
          {/* Requested Items (for photo requests) */}
          {request.requested_items?.length > 0 && (
            <div className="bg-[#121212] rounded-xl p-4 border border-[#1a1a1a]">
              <h4 className="text-white font-medium mb-3">Elementi richiesti</h4>
              <div className="flex flex-wrap gap-2">
                {request.requested_items.map((item, i) => (
                  <span key={i} className="text-sm text-white/70 bg-white/5 px-3 py-1 rounded">
                    {item.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}
          
          {/* Response */}
          {request.response_message && (
            <div className="bg-[#B08B5C]/10 rounded-xl p-4 border border-[#B08B5C]/30">
              <h4 className="text-[#B08B5C] font-medium mb-2">La tua risposta</h4>
              <p className="text-white/70 whitespace-pre-wrap">{request.response_message}</p>
              {request.responded_at && (
                <p className="text-white/30 text-xs mt-2">
                  Risposto il {new Date(request.responded_at).toLocaleString('it-IT')}
                </p>
              )}
            </div>
          )}
        </div>
        
        {/* Actions */}
        <div className="p-6 border-t border-[#1a1a1a] flex gap-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-[#1a1a1a] text-white/60"
          >
            Chiudi
          </Button>
          {request.status !== 'responded' && request.status !== 'closed' && (
            <Button
              onClick={() => onRespond(request)}
              className="flex-1 bg-[#B08B5C] text-black hover:bg-[#C9A46E]"
            >
              <Send className="w-4 h-4 mr-2" /> Rispondi
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Respond Modal
 */
const RespondModal = ({ request, onClose, onSubmit }) => {
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  if (!request) return null;
  
  const handleSubmit = async () => {
    if (!message.trim()) {
      toast.error('Inserisci un messaggio');
      return;
    }
    
    setIsSubmitting(true);
    try {
      await onSubmit(request.id, message);
      toast.success('Risposta inviata!');
      onClose();
    } catch (err) {
      toast.error('Errore nell\'invio');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
      <div className="bg-[#0a0a0a] rounded-2xl border border-[#1a1a1a] w-full max-w-lg">
        <div className="flex items-center justify-between p-6 border-b border-[#1a1a1a]">
          <div>
            <h3 className="text-lg font-semibold text-white">Rispondi alla richiesta</h3>
            <p className="text-white/40 text-sm">A: {request.contact_name}</p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6">
          <label className="text-sm text-white/50 block mb-2">Messaggio di risposta</label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={6}
            placeholder="Scrivi la tua risposta..."
            className="w-full px-4 py-3 rounded-lg bg-[#121212] border border-[#1a1a1a] text-white placeholder-white/30 focus:border-[#B08B5C] focus:outline-none resize-none"
          />
          
          <div className="flex gap-3 mt-4">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1 border-[#1a1a1a] text-white/60"
            >
              Annulla
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1 bg-[#B08B5C] text-black hover:bg-[#C9A46E]"
            >
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Invia risposta
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Material Card Component
 */
const MaterialCard = ({ material, onEdit, onDelete }) => (
  <div className="bg-[#121212] border border-[#1a1a1a] rounded-xl p-4 hover:border-[#B08B5C]/30 transition-all">
    <div className="flex items-start gap-3">
      <div className="w-16 h-16 rounded-lg bg-[#0a0a0a] flex items-center justify-center overflow-hidden">
        <Package className="w-6 h-6 text-white/20" />
      </div>
      <div className="flex-1 min-w-0">
        <h4 className="text-white font-medium truncate">{material.material_name}</h4>
        <p className="text-white/40 text-sm capitalize">{material.material_type}</p>
        <div className="flex items-center gap-2 mt-1">
          <div className={`w-2 h-2 rounded-full ${material.is_available ? 'bg-green-500' : 'bg-red-500'}`}></div>
          <span className="text-xs text-white/50">
            {material.availability_status === 'in_stock' ? 'Disponibile' : 
             material.availability_status === 'limited' ? 'Limitato' :
             material.availability_status === 'on_order' ? 'Su ordine' : 'Esaurito'}
          </span>
        </div>
      </div>
      <div className="flex gap-1">
        <button 
          onClick={() => onEdit(material)}
          className="p-2 text-white/40 hover:text-white hover:bg-white/5 rounded-lg"
        >
          <Edit className="w-4 h-4" />
        </button>
        <button 
          onClick={() => onDelete(material)}
          className="p-2 text-white/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </div>
    {material.quantity_indication && (
      <p className="text-[#B08B5C] text-xs mt-2">{material.quantity_indication}</p>
    )}
  </div>
);

/**
 * Main Professional Dashboard Component
 */
const ProfessionalDashboard = () => {
  const navigate = useNavigate();
  const { user: authUser, profile } = useAuth();
  
  // State
  const [activeTab, setActiveTab] = useState('overview');
  const [isLoading, setIsLoading] = useState(true);
  const [professional, setProfessional] = useState(null);
  const [stats, setStats] = useState({});
  const [requests, setRequests] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [slabs, setSlabs] = useState([]);
  const [analytics, setAnalytics] = useState({ totals: {}, daily: [] });
  
  // Modals
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showRespondModal, setShowRespondModal] = useState(false);
  const [respondTarget, setRespondTarget] = useState(null);
  
  // Filters
  const [requestFilter, setRequestFilter] = useState('all');
  
  // Load professional profile
  useEffect(() => {
    const loadProfessional = async () => {
      if (!authUser?.id) return;
      
      setIsLoading(true);
      try {
        // Get professional linked to this user
        const { data, error } = await supabase
          .from('professionals')
          .select('*')
          .eq('owner_user_id', authUser.id)
          .single();
        
        if (error) {
          // No professional profile, redirect to retail-advisor
          toast.error('Profilo professionale non trovato');
          navigate('/retail-advisor');
          return;
        }
        
        setProfessional(data);
      } catch (err) {
        console.error('Error loading professional:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadProfessional();
  }, [authUser?.id, navigate]);
  
  // Load dashboard data
  useEffect(() => {
    if (!professional?.id) return;
    
    const loadDashboardData = async () => {
      try {
        // Load requests
        const { data: reqData } = await supabase
          .from('material_requests')
          .select('*')
          .eq('professional_id', professional.id)
          .order('created_at', { ascending: false });
        
        setRequests(reqData || []);
        
        // Load materials
        const { data: matData } = await supabase
          .from('professional_materials')
          .select('*')
          .eq('professional_id', professional.id)
          .order('material_name');
        
        setMaterials(matData || []);
        
        // Load slab photos
        const { data: slabData } = await supabase
          .from('professional_slab_files')
          .select('*')
          .eq('professional_id', professional.id)
          .eq('status', 'active')
          .order('created_at', { ascending: false });
        
        setSlabs(slabData || []);
        
        // Calculate stats
        const newRequests = (reqData || []).filter(r => r.status === 'new').length;
        const pendingRequests = (reqData || []).filter(r => ['new', 'read', 'in_progress'].includes(r.status)).length;
        
        setStats({
          new_requests: newRequests,
          pending_requests: pendingRequests,
          total_requests: (reqData || []).length,
          total_materials: (matData || []).length,
          total_slabs: (slabData || []).length
        });
        
      } catch (err) {
        console.error('Error loading dashboard data:', err);
      }
    };
    
    loadDashboardData();
  }, [professional?.id]);
  
  // Handle respond to request
  const handleRespond = async (requestId, message) => {
    const { error } = await supabase
      .from('material_requests')
      .update({
        status: 'responded',
        response_message: message,
        responded_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      })
      .eq('id', requestId)
      .eq('professional_id', professional.id);
    
    if (error) throw error;
    
    // Reload requests
    const { data } = await supabase
      .from('material_requests')
      .select('*')
      .eq('professional_id', professional.id)
      .order('created_at', { ascending: false });
    
    setRequests(data || []);
  };
  
  // Handle update request status
  const handleUpdateStatus = async (requestId, newStatus) => {
    const { error } = await supabase
      .from('material_requests')
      .update({
        status: newStatus,
        updated_at: new Date().toISOString(),
        ...(newStatus === 'read' ? { read_at: new Date().toISOString() } : {})
      })
      .eq('id', requestId)
      .eq('professional_id', professional.id);
    
    if (error) {
      toast.error('Errore nell\'aggiornamento');
      return;
    }
    
    toast.success('Stato aggiornato');
    
    // Reload requests
    const { data } = await supabase
      .from('material_requests')
      .select('*')
      .eq('professional_id', professional.id)
      .order('created_at', { ascending: false });
    
    setRequests(data || []);
  };
  
  // Handle delete material
  const handleDeleteMaterial = async (material) => {
    if (!confirm(`Rimuovere "${material.material_name}" dall'inventario?`)) return;
    
    const { error } = await supabase
      .from('professional_materials')
      .delete()
      .eq('id', material.id)
      .eq('professional_id', professional.id);
    
    if (error) {
      toast.error('Errore nella rimozione');
      return;
    }
    
    toast.success('Materiale rimosso');
    setMaterials(prev => prev.filter(m => m.id !== material.id));
  };
  
  // Filter requests
  const filteredRequests = requestFilter === 'all' 
    ? requests 
    : requests.filter(r => r.status === requestFilter);
  
  // Navigation tabs
  const tabs = [
    { id: 'overview', label: 'Panoramica', icon: LayoutDashboard },
    { id: 'requests', label: 'Richieste', icon: MessageSquare, badge: stats.new_requests },
    { id: 'materials', label: 'Materiali', icon: Package },
    { id: 'slabs', label: 'Foto lastre', icon: Camera },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 }
  ];
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#B08B5C]" />
      </div>
    );
  }
  
  if (!professional) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Building2 className="w-16 h-16 text-white/10 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">Nessun profilo professionale</h2>
            <p className="text-white/50 mb-6">Non hai ancora un profilo professionale attivo.</p>
            <Button 
              onClick={() => navigate('/retail-advisor')}
              className="bg-[#B08B5C] text-black hover:bg-[#C9A46E]"
            >
              Vai alla directory
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col">
      <Header />
      
      <main className="flex-1 pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-white">{professional.business_name}</h1>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-sm text-white/50 capitalize">{professional.professional_type}</span>
                {professional.is_verified && (
                  <span className="flex items-center gap-1 text-[#B08B5C] text-sm">
                    <Check className="w-4 h-4" /> Verificato
                  </span>
                )}
                {professional.rating > 0 && (
                  <span className="flex items-center gap-1 text-white/50 text-sm">
                    <Star className="w-4 h-4 text-[#B08B5C] fill-[#B08B5C]" /> 
                    {professional.rating.toFixed(1)}
                  </span>
                )}
              </div>
            </div>
            <Button
              variant="outline"
              onClick={() => navigate('/settings')}
              className="border-[#1a1a1a] text-white/60 hover:bg-[#1a1a1a]"
            >
              <Settings className="w-4 h-4 mr-2" /> Impostazioni
            </Button>
          </div>
          
          {/* Navigation Tabs */}
          <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
            {tabs.map(tab => {
              const TabIcon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm whitespace-nowrap transition-colors ${
                    activeTab === tab.id 
                      ? 'bg-[#B08B5C] text-black' 
                      : 'bg-[#121212] text-white/60 hover:text-white border border-[#1a1a1a]'
                  }`}
                >
                  <TabIcon className="w-4 h-4" />
                  {tab.label}
                  {tab.badge > 0 && (
                    <span className={`px-1.5 py-0.5 rounded-full text-xs ${
                      activeTab === tab.id ? 'bg-black/20 text-black' : 'bg-[#B08B5C] text-black'
                    }`}>
                      {tab.badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Stats */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard 
                  title="Nuove richieste" 
                  value={stats.new_requests} 
                  icon={MessageSquare}
                />
                <StatCard 
                  title="Richieste totali" 
                  value={stats.total_requests} 
                  icon={Users}
                />
                <StatCard 
                  title="Materiali dichiarati" 
                  value={stats.total_materials} 
                  icon={Package}
                />
                <StatCard 
                  title="Foto lastre" 
                  value={stats.total_slabs} 
                  icon={Camera}
                />
              </div>
              
              {/* Recent Requests */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-white">Richieste recenti</h2>
                  <button 
                    onClick={() => setActiveTab('requests')}
                    className="text-sm text-[#B08B5C] hover:underline flex items-center gap-1"
                  >
                    Vedi tutte <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                
                {requests.length === 0 ? (
                  <div className="bg-[#121212] rounded-xl border border-[#1a1a1a] p-8 text-center">
                    <MessageSquare className="w-12 h-12 text-white/10 mx-auto mb-4" />
                    <p className="text-white/50">Nessuna richiesta ricevuta</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {requests.slice(0, 3).map(req => (
                      <RequestCard
                        key={req.id}
                        request={req}
                        onView={setSelectedRequest}
                        onRespond={(r) => {
                          setRespondTarget(r);
                          setShowRespondModal(true);
                        }}
                        onUpdateStatus={handleUpdateStatus}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Requests Tab */}
          {activeTab === 'requests' && (
            <div className="space-y-6">
              {/* Filters */}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {[
                  { id: 'all', label: 'Tutte' },
                  { id: 'new', label: 'Nuove' },
                  { id: 'read', label: 'Lette' },
                  { id: 'in_progress', label: 'In corso' },
                  { id: 'responded', label: 'Risposte' },
                  { id: 'closed', label: 'Chiuse' }
                ].map(filter => (
                  <button
                    key={filter.id}
                    onClick={() => setRequestFilter(filter.id)}
                    className={`px-4 py-2 rounded-lg text-sm whitespace-nowrap transition-colors ${
                      requestFilter === filter.id 
                        ? 'bg-[#B08B5C] text-black' 
                        : 'bg-[#121212] text-white/60 hover:text-white border border-[#1a1a1a]'
                    }`}
                  >
                    {filter.label}
                    {filter.id !== 'all' && (
                      <span className="ml-1 text-xs opacity-70">
                        ({requests.filter(r => r.status === filter.id).length})
                      </span>
                    )}
                  </button>
                ))}
              </div>
              
              {/* Request List */}
              {filteredRequests.length === 0 ? (
                <div className="bg-[#121212] rounded-xl border border-[#1a1a1a] p-12 text-center">
                  <MessageSquare className="w-16 h-16 text-white/10 mx-auto mb-4" />
                  <p className="text-white/50">Nessuna richiesta trovata</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredRequests.map(req => (
                    <RequestCard
                      key={req.id}
                      request={req}
                      onView={setSelectedRequest}
                      onRespond={(r) => {
                        setRespondTarget(r);
                        setShowRespondModal(true);
                      }}
                      onUpdateStatus={handleUpdateStatus}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Materials Tab */}
          {activeTab === 'materials' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <p className="text-white/50">{materials.length} materiali dichiarati</p>
                <Button className="bg-[#B08B5C] text-black hover:bg-[#C9A46E]">
                  <Plus className="w-4 h-4 mr-2" /> Aggiungi materiale
                </Button>
              </div>
              
              {materials.length === 0 ? (
                <div className="bg-[#121212] rounded-xl border border-[#1a1a1a] p-12 text-center">
                  <Package className="w-16 h-16 text-white/10 mx-auto mb-4" />
                  <p className="text-white/50 mb-4">Nessun materiale dichiarato</p>
                  <Button className="bg-[#B08B5C] text-black hover:bg-[#C9A46E]">
                    <Plus className="w-4 h-4 mr-2" /> Aggiungi il primo materiale
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {materials.map(mat => (
                    <MaterialCard
                      key={mat.id}
                      material={mat}
                      onEdit={() => toast.info('Modifica in arrivo')}
                      onDelete={handleDeleteMaterial}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Slabs Tab */}
          {activeTab === 'slabs' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <p className="text-white/50">{slabs.length} foto caricate</p>
                <Button className="bg-[#B08B5C] text-black hover:bg-[#C9A46E]">
                  <Upload className="w-4 h-4 mr-2" /> Carica foto
                </Button>
              </div>
              
              {slabs.length === 0 ? (
                <div className="bg-[#121212] rounded-xl border border-[#1a1a1a] p-12 text-center">
                  <Camera className="w-16 h-16 text-white/10 mx-auto mb-4" />
                  <p className="text-white/50 mb-2">Nessuna foto lastra caricata</p>
                  <p className="text-white/30 text-sm mb-4">
                    Carica foto HD delle tue lastre per rispondere alle richieste
                  </p>
                  <Button className="bg-[#B08B5C] text-black hover:bg-[#C9A46E]">
                    <Upload className="w-4 h-4 mr-2" /> Carica la prima foto
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {slabs.map(slab => (
                    <div 
                      key={slab.id} 
                      className="aspect-square rounded-xl overflow-hidden bg-[#121212] border border-[#1a1a1a] group relative"
                    >
                      <img 
                        src={slab.file_url} 
                        alt={slab.material_name || 'Slab'} 
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                        <button className="p-2 bg-white/10 rounded-lg hover:bg-white/20">
                          <Eye className="w-4 h-4 text-white" />
                        </button>
                        <button className="p-2 bg-red-500/20 rounded-lg hover:bg-red-500/30">
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                      {slab.material_name && (
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                          <p className="text-white text-xs truncate">{slab.material_name}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              <div className="bg-[#121212] rounded-xl border border-[#1a1a1a] p-8 text-center">
                <BarChart3 className="w-16 h-16 text-white/10 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-white mb-2">Analytics in arrivo</h3>
                <p className="text-white/50">
                  Monitora visualizzazioni, click e conversioni del tuo profilo
                </p>
              </div>
            </div>
          )}
          
        </div>
      </main>
      
      <Footer />
      
      {/* Modals */}
      {selectedRequest && (
        <RequestDetailModal
          request={selectedRequest}
          onClose={() => setSelectedRequest(null)}
          onRespond={(r) => {
            setSelectedRequest(null);
            setRespondTarget(r);
            setShowRespondModal(true);
          }}
          onUpdateStatus={handleUpdateStatus}
        />
      )}
      
      {showRespondModal && (
        <RespondModal
          request={respondTarget}
          onClose={() => {
            setShowRespondModal(false);
            setRespondTarget(null);
          }}
          onSubmit={handleRespond}
        />
      )}
    </div>
  );
};

export default ProfessionalDashboard;
